import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useArticleContext } from '../context/ArticleContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { FiEdit3, FiTrash2, FiPlus, FiFileText, FiClock, FiLoader, FiDatabase, FiRefreshCw, FiUsers, FiFilter, FiGrid, FiUser } = FiIcons;

const ArticleList = () => {
  const { articles, loading, error, deleteArticle, refetch } = useArticleContext();
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [organizationMode, setOrganizationMode] = useState('client'); // 'client' or 'user'
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'list'

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This will remove it from Supabase permanently.`)) {
      try {
        await deleteArticle(id);
      } catch (err) {
        alert('Failed to delete article: ' + err.message);
      }
    }
  };

  const getWordCount = (content) => {
    if (!content) return 0;
    return content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleRefresh = () => {
    refetch();
  };

  // Group articles by owner/client
  const groupedByOwner = articles.reduce((groups, article) => {
    const owner = article.owner || 'Unassigned';
    if (!groups[owner]) {
      groups[owner] = [];
    }
    groups[owner].push(article);
    return groups;
  }, {});

  // Group articles by user
  const groupedByUser = articles.reduce((groups, article) => {
    const user = article.associated_user || 'Unassigned User';
    if (!groups[user]) {
      groups[user] = [];
    }
    groups[user].push(article);
    return groups;
  }, {});

  // Get unique owners and users for filters
  const owners = ['all', ...Object.keys(groupedByOwner).sort()];
  const users = ['all', ...Object.keys(groupedByUser).sort()];

  // Filter articles based on selected criteria
  const getFilteredArticles = () => {
    let filtered = articles;

    if (selectedOwner !== 'all') {
      filtered = filtered.filter(article => (article.owner || 'Unassigned') === selectedOwner);
    }

    if (selectedUser !== 'all') {
      filtered = filtered.filter(article => (article.associated_user || 'Unassigned User') === selectedUser);
    }

    return filtered;
  };

  const filteredArticles = getFilteredArticles();

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-16">
          <SafeIcon icon={FiLoader} className="text-6xl text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Loading Articles</h3>
          <p className="text-gray-500">Fetching from Supabase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-16 bg-white rounded-lg border border-red-200">
          <SafeIcon icon={FiDatabase} className="text-6xl text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-700 mb-2">Database Connection Error</h3>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className="text-lg" />
            <span>Retry Connection</span>
          </button>
        </div>
      </div>
    );
  }

  const renderArticleCard = (article) => (
    <div key={article.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">
                {article.title || 'Untitled Article'}
              </h3>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                ID: {article.id}
              </span>
              {viewMode === 'list' && (
                <>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                    {article.owner || 'Unassigned'}
                  </span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {article.associated_user || 'Unassigned User'}
                  </span>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-500 mb-4">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiClock} className="text-sm" />
                <span>
                  Updated {article.updated_at ? format(new Date(article.updated_at), 'MMM d, yyyy') : 'Unknown'}
                </span>
              </div>
              <div>
                <span>{getWordCount(article.content)} words</span>
              </div>
            </div>

            {/* Show client and user info in cards */}
            <div className="flex items-center space-x-4 mb-3">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUsers} className="text-sm text-purple-600" />
                <span className="text-sm text-gray-600">Client:</span>
                <span className="text-sm font-medium text-gray-800">{article.owner || 'Unassigned'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUser} className="text-sm text-green-600" />
                <span className="text-sm text-gray-600">User:</span>
                <span className="text-sm font-medium text-gray-800">{article.associated_user || 'Unassigned User'}</span>
              </div>
            </div>

            {article.title_tag && (
              <div className="mb-2">
                <span className="text-xs font-medium text-gray-600">Title Tag: </span>
                <span className="text-sm text-gray-800">{article.title_tag}</span>
              </div>
            )}

            {article.meta_description && (
              <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                {article.meta_description}
              </p>
            )}

            <div className="flex items-center space-x-3">
              <Link
                to={`/editor/${article.id}`}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiEdit3} className="text-sm" />
                <span>Edit</span>
              </Link>
              
              <button
                onClick={() => handleDelete(article.id, article.title)}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGroupedView = () => {
    const groups = organizationMode === 'client' ? groupedByOwner : groupedByUser;
    const icon = organizationMode === 'client' ? FiUsers : FiUser;
    const label = organizationMode === 'client' ? 'Client' : 'User';

    return (
      <div className="space-y-8">
        {Object.keys(groups).sort().map(groupKey => (
          <div key={groupKey} className="space-y-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={icon} className="text-xl text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">{groupKey}</h2>
              <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                {groups[groupKey].length} articles
              </span>
              <span className="text-xs text-gray-500">
                ({label})
              </span>
            </div>
            <div className="grid gap-6">
              {groups[groupKey].map(renderArticleCard)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <span>Supabase Articles</span>
            <SafeIcon icon={FiDatabase} className="text-2xl text-blue-600" />
          </h1>
          <p className="text-gray-600 mt-1">
            Manage SEO articles synced with Supabase • {articles.length} articles • {owners.length - 1} clients • {users.length - 1} users
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className="text-lg" />
            <span>Refresh</span>
          </button>
          
          <Link
            to="/editor"
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
            <span>New Article</span>
          </Link>
        </div>
      </div>

      {/* Enhanced Filters and View Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="space-y-4">
          {/* Organization Mode Selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Organize by:</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setOrganizationMode('client')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    organizationMode === 'client' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <SafeIcon icon={FiUsers} className="text-sm" />
                  <span>Client</span>
                </button>
                <button
                  onClick={() => setOrganizationMode('user')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    organizationMode === 'user' 
                      ? 'bg-green-100 text-green-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <SafeIcon icon={FiUser} className="text-sm" />
                  <span>User</span>
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <button
                onClick={() => setViewMode('grouped')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'grouped' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiUsers} className="text-sm" />
                <span>Grouped</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiGrid} className="text-sm" />
                <span>List</span>
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiFilter} className="text-lg text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiUsers} className="text-sm text-purple-600" />
              <span className="text-sm text-gray-600">Client:</span>
              <select
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Clients ({articles.length})</option>
                {Object.keys(groupedByOwner).sort().map(owner => (
                  <option key={owner} value={owner}>
                    {owner} ({groupedByOwner[owner].length})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiUser} className="text-sm text-green-600" />
              <span className="text-sm text-gray-600">User:</span>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Users ({articles.length})</option>
                {Object.keys(groupedByUser).sort().map(user => (
                  <option key={user} value={user}>
                    {user} ({groupedByUser[user].length})
                  </option>
                ))}
              </select>
            </div>

            {(selectedOwner !== 'all' || selectedUser !== 'all') && (
              <button
                onClick={() => {
                  setSelectedOwner('all');
                  setSelectedUser('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <SafeIcon icon={FiFileText} className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No articles in Supabase</h3>
          <p className="text-gray-500 mb-6">Create your first article to get started with Supabase sync</p>
          <Link
            to="/editor"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
            <span>Create Article</span>
          </Link>
        </div>
      ) : (
        <div>
          {viewMode === 'grouped' && (selectedOwner === 'all' && selectedUser === 'all') ? (
            renderGroupedView()
          ) : (
            // List view or filtered view
            <div className="grid gap-6">
              {filteredArticles.map(renderArticleCard)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleList;